import type { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

import type { DigitalMediaPlan } from '~/modules/artist-roster/pages/artist-details/pages/media-planner/types';
import type { ArtistDetails } from '~/modules/artist-roster/types';
import { BREADCRUMBS_PROPS } from '~/modules/constants';
import { Breadcrumbs } from '~/modules/navigation/components/Breadcrumbs';

interface MediaPlanBreadcrumbsProps extends PropsWithChildren {
  artistData: ArtistDetails;
  setPlan: (plan?: DigitalMediaPlan) => void;
}

export function MediaPlanBreadcrumbs({
  artistData,
  setPlan,
  children,
}: MediaPlanBreadcrumbsProps) {
  const { t } = useTranslation('campaign-planner', {
    keyPrefix: 'digitalCampaigns',
  });

  return (
    <div className="mb-md">
      <Breadcrumbs.Root {...BREADCRUMBS_PROPS.ROOT}>
        <Breadcrumbs.Item to="/roster" {...BREADCRUMBS_PROPS.ITEM_LINK}>
          {t('breadcrumbs.roster')}
        </Breadcrumbs.Item>
        <Breadcrumbs.Item
          to={`/roster/${artistData.partyId}`}
          {...BREADCRUMBS_PROPS.ITEM_LINK}
        >
          {artistData.name}
        </Breadcrumbs.Item>
        <Breadcrumbs.Item
          to={`/roster/${artistData.partyId}/campaigns/planner`}
          onClick={
            // @TODO WMG1-818 - Need to hook up to the unsaved changes modal.
            () => {
              // We're going back to the Wizard from the Media Plan. The user
              // will have already been prompted to save or discard their
              // changes so if we get here, they've discarded their changes so
              // we just clear the plan.
              setPlan(undefined);
            }
          }
          {...BREADCRUMBS_PROPS.ITEM_LINK}
        >
          {t('breadcrumbs.digitalMediaPlanner')}
        </Breadcrumbs.Item>
        {children ? <Breadcrumbs.Item>{children}</Breadcrumbs.Item> : null}
      </Breadcrumbs.Root>
    </div>
  );
}

MediaPlanBreadcrumbs.displayName = 'MediaPlanBreadcrumbs';
